import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  GoogleMap,
  useLoadScript,
  Autocomplete,
  OverlayViewF,
} from "@react-google-maps/api";
import {
  Address,
  Container,
  Description,
  Load,
  Name,
  Photo,
  Redes,
  RedesImage,
  SearchContainer,
  Select,
  TotalPartners,
  ViewInput,
  WarningTag,
} from "./styles";
import api from "../services/api";
import { toast } from "react-toastify";
import { Button, Input, Modal, Spin } from "antd";
import { redesSociais } from "../Const";
import { MdOutlineFilterList } from "react-icons/md";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import defaultUserImage from "../images/user.png";

const libraries = ["places"];
const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const Tooltip = styled.div`
  visibility: hidden;
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translate(-50%, -10px);
  background-color: #ffffff;
  border-left: 4px solid #20D072;
  color: #141414;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 11px;
  font-family: "Montserrat-Medium";
  width: max-content;
  max-width: 250px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  opacity: 0;
  transition: opacity 0.15s ease-in-out, transform 0.15s ease-in-out;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  text-align: left;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  border-right: 1px solid rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  gap: 3px;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -6px;
    border-width: 6px;
    border-style: solid;
    border-color: #ffffff transparent transparent transparent;
    filter: drop-shadow(0px 2px 1px rgba(0, 0, 0, 0.1));
  }
`;

const PinContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  filter: drop-shadow(0px 3px 5px rgba(0, 0, 0, 0.4));
  transition: transform 0.15s ease-in-out;
  will-change: transform;
  position: relative;

  &:hover {
    transform: scale(1.15);
    z-index: 999;
  }

  &:hover ${Tooltip} {
    visibility: visible;
    opacity: 1;
    transform: translate(-50%, -6px);
  }
`;

const PinBubble = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 3px solid #20D072;
  background-color: #fff;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
`;

const PinImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PinTail = styled.div`
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 8px solid #20D072;
  margin-top: -2px;
`;

const PartnerMarker = React.memo(({ marker, onClick, defaultUserImage }) => {
  const handleClick = useCallback(() => {
    onClick(marker);
  }, [marker, onClick]);

  return (
    <OverlayViewF
      position={{ lat: marker.latitude, lng: marker.longitude }}
      mapPaneName="overlayMouseTarget"
      getPixelPositionOffset={(width, height) => ({
        x: -(width / 2),
        y: -height,
      })}
    >
      <PinContainer onClick={handleClick}>
        <Tooltip>
          <strong style={{ display: "block", fontSize: "12px", color: "#141414", fontFamily: "Montserrat-SemiBold", lineHeight: "1.2" }}>
            {marker.label}
          </strong>
          {marker.description && (
            <span style={{ display: "block", fontSize: "10px", color: "#666", fontFamily: "Montserrat-Regular", whiteSpace: "normal", lineHeight: "1.3" }}>
              {marker.description}
            </span>
          )}
          {(marker.street || marker.neighborhood) && (
            <span style={{ display: "block", fontSize: "9px", color: "#888", fontFamily: "Montserrat-Medium", whiteSpace: "normal", lineHeight: "1.2" }}>
              {marker.street && `${marker.street}${marker.number ? `, ${marker.number}` : ""}`}
              {marker.neighborhood && `${marker.street ? " - " : ""}${marker.neighborhood}`}
              {marker.city && ` (${marker.city})`}
            </span>
          )}
        </Tooltip>
        <PinBubble>
          <PinImage src={marker.photo_url || defaultUserImage} alt={marker.label} loading="lazy" />
        </PinBubble>
        <PinTail />
      </PinContainer>
    </OverlayViewF>
  );
});

export default function Mapps() {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyD8zV3OvIQtzv2Lv_kEE9zMWPirD8tzpTM", // Insira sua chave de API do Google Maps aqui
    libraries,
  });

  const [searchParams] = useSearchParams();

  // Pega valores da query string
  const keywordsQ = searchParams.get("keywords");
  const categoriesQ = searchParams.get("categories");
  const categoriesFilterQ = searchParams.get("categoriesFilter");
  const filterQ = searchParams.get("filter");
  const location = searchParams.get("location");
  const latitude = searchParams.get("latitude");
  const longitude = searchParams.get("longitude");
  const zoomC = searchParams.get("zoom");

  const mapRef = useRef(null);

  const [regiao, setRegiao] = useState({
    lat: -19.919052,
    lng: -46.633308,
  });
  const [regiaoMax, setRegiaoMax] = useState(null);
  const [zoom, setZoom] = useState(zoomC ? Number(zoomC) : 14);
  const [autocomplete, setAutocomplete] = useState(null);

  // Estado para o modal de filtros
  const [visibleModalFilter, setVisibleModalFilter] = useState(false);
  const [visibleModal, setVisibleModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [keywordsSearch, setKeywordsSearch] = useState(keywordsQ || "");
  const [keywords, setKeywords] = useState("");
  const [categoriesSearch, setCategoriesSearch] = useState(
    categoriesQ ? categoriesQ.split(",") : []
  );
  const [categoriesFilter, setCategoriesFilter] = useState([]);

  const [partner, setPartner] = useState({});
  const [partners, setPartners] = useState([]);

  const [isLoading, setIsLoading] = useState(true);

  const handleMarkerClick = useCallback((marker) => {
    setPartner(marker);
    setVisibleModal(true);
  }, []);

  const displayedPartners = useMemo(() => {
    if (!partners.length) return [];
    if (!regiaoMax) return partners.slice(0, 75);

    const centerLat = (regiaoMax.minLatitude + regiaoMax.maxLatitude) / 2;
    const centerLng = (regiaoMax.minLongitude + regiaoMax.maxLongitude) / 2;

    const sorted = [...partners].sort((a, b) => {
      const distA = Math.pow(a.latitude - centerLat, 2) + Math.pow(a.longitude - centerLng, 2);
      const distB = Math.pow(b.latitude - centerLat, 2) + Math.pow(b.longitude - centerLng, 2);
      return distA - distB;
    });

    return sorted.slice(0, 75);
  }, [partners, regiaoMax]);

  useEffect(() => {
    if (location == "false") {
      if (latitude && longitude) {
        setRegiao({
          lat: Number(latitude),
          lng: Number(longitude),
        });
      }
    } else {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const region = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          setRegiao(region);
        },
        () => {
          toast.warn("Não foi possível obter sua localização.");
        }
      );
    }

    setTimeout(() => {
      const bounds = mapRef.current?.getBounds();
      if (bounds) {
        const ne = bounds.getNorthEast(); // canto superior direito
        const sw = bounds.getSouthWest(); // canto inferior esquerdo

        const minLatitude = sw.lat();
        const maxLatitude = ne.lat();
        const minLongitude = sw.lng();
        const maxLongitude = ne.lng();

        // Envie isso ao back-end ou filtre localmente
        const regiaoMax = {
          minLatitude,
          maxLatitude,
          minLongitude,
          maxLongitude,
        };
        setRegiaoMax(regiaoMax);
        searchPartners(regiaoMax, keywords, categoriesSearch.join(","));
      }
    }, 500);

    loadingCategories();
  }, []);

  useEffect(() => {
    if (regiaoMax) {
      if (zoom >= 11) {
        searchPartners(regiaoMax, keywordsSearch, categoriesSearch.join(","));
      } else {
        setPartners([]);
      }
    }
  }, [regiaoMax, zoom, keywordsSearch, categoriesSearch]);

  async function loadingCategories() {
    await api
      .get(`/categories`)
      .then((response) => {
        setCategories(response.data);
      })
      .catch((error) => {
        toast.warning("Ocorreu um erro ao carregar categorias");
      });
  }

  async function searchPartners(regiao, keywords, categories) {
    setIsLoading(true);
    await api
      .get(
        `/maps/partners?minLatitude=${regiao.minLatitude}&maxLatitude=${
          regiao.maxLatitude
        }&minLongitude=${regiao.minLongitude}&maxLongitude=${
          regiao.maxLongitude
        }&keywords=${keywords}${
          categories.length ? `&categories=${categories}` : ""
        }`
      )
      .then((response) => {
        setPartners(response.data);
      })
      .catch((error) => {
        toast.error("Ocorreu um erro, verifique sua conexão e tente novamente");
      });
    setIsLoading(false);
  }

  const onLoad = (autocomplete) => {
    setAutocomplete(autocomplete);
  };

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        const newCenter = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        setRegiao(newCenter);
        mapRef.current?.setZoom(14);
      }
    } else {
      console.log("O Autocomplete não está carregado ainda!");
    }
  };

  if (loadError) return "Erro ao carregar o mapa";
  if (!isLoaded) return "Carregando Mapa...";

  return (
    <Container>
      <SearchContainer
        style={{
          position: "absolute",
          top: 15,
          left: 15,
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          width: `calc(100% - 30px)`,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "row",
            width: "100%",
            maxWidth: 380,
          }}
        >
          <Autocomplete
            className="input"
            onLoad={onLoad}
            onPlaceChanged={onPlaceChanged}
          >
            <Input
              type="text"
              placeholder="procurar endereço"
              style={{
                boxSizing: `border-box`,
                border: `1px solid transparent`,
                marginRight: 15,
                width: `100%`,
                maxWidth: 300,
                height: `32px`,
                padding: `0 12px`,
                borderRadius: `3px`,
                boxShadow: `0 2px 6px rgba(0, 0, 0, 0.3)`,
                fontSize: `14px`,
                display: "block",
                textOverflow: `ellipses`,
              }}
            />
          </Autocomplete>
          {filterQ != "false" && (
            <Button
              type="primary"
              style={{
                boxSizing: `border-box`,
                border: `1px solid transparent`,
                boxShadow: `0 2px 6px rgba(0, 0, 0, 0.3)`,
                marginLeft: 15,
              }}
              onClick={() => {
                setKeywords(keywordsSearch);
                setCategoriesFilter(categoriesSearch);
                setVisibleModalFilter(true);
              }}
            >
              <MdOutlineFilterList size={22} />
              Filtros
            </Button>
          )}
        </div>

        {zoom < 11 ? (
          <WarningTag>Aproxime o mapa para fazer uma busca</WarningTag>
        ) : (
          <TotalPartners>
            {partners.length
              ? `${
                  partners.length +
                  ` parceiro${partners.length > 1 ? "s" : ""} encontrado${
                    partners.length > 1 ? "s" : ""
                  }`
                }`
              : "nenhum parceiro encontrado"}
          </TotalPartners>
        )}
      </SearchContainer>

      {/* Modal de Filtros */}
      <Modal
        title="Filtros"
        width={500}
        open={visibleModalFilter}
        cancelText={"FECHAR"}
        okText="APLICAR FILTROS"
        onCancel={() => {
          setVisibleModalFilter(false);
          setPartner(null);
        }}
        onOk={() => {
          setCategoriesSearch(categoriesFilter);
          setKeywordsSearch(keywords);
          setVisibleModalFilter(false);
        }}
      >
        <ViewInput style={{ marginTop: 25 }}>
          <p>Buscar por</p>
          <Input
            value={keywords}
            placeholder="buscar por nome ou palavras chaves"
            style={{
              height: 35,
              fontSize: 15,
            }}
            onChange={(e) => {
              setKeywords(e.target.value.replace(/ /g, "").toLowerCase());
            }}
          />
        </ViewInput>
        <ViewInput style={{ marginTop: 10, marginBottom: 25 }}>
          <p>Categorias</p>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              marginBottom: 10,
              maxHeight: 150,
              overflowY: "auto",
            }}
          >
            <Select
              select={!categoriesFilter.length}
              onClick={() => {
                setCategoriesFilter([]);
              }}
            >
              TODAS
            </Select>
            {categories.map((item) => {
              if (!categoriesFilterQ || categoriesFilterQ.includes(item.id)) {
                return (
                  <Select
                    key={item.id}
                    select={categoriesFilter.includes(item.id)}
                    onClick={() => {
                      if (categoriesFilter.includes(item.id)) {
                        let categoriasC = categoriesFilter.filter((data) => {
                          return data != item.id;
                        });
                        setCategoriesFilter(categoriasC);
                      } else {
                        let categoriasC = [...categoriesFilter];
                        categoriasC.push(item.id);
                        setCategoriesFilter(categoriasC);
                      }
                    }}
                  >
                    {item.name}
                  </Select>
                );
              }
              return null;
            })}
          </div>
        </ViewInput>
      </Modal>

      <Modal
        title="Parceiro"
        width={500}
        open={visibleModal}
        cancelText={"FECHAR"}
        okButtonProps={{
          style: {
            display: "none",
          },
        }}
        cancelButtonProps={{
          style: {
            display: "none",
          },
        }}
        onCancel={() => {
          setVisibleModal(false);
          setPartner(null);
        }}
      >
        <div
          style={{
            width: "100%",
            margin: "25px 0",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {partner && <Photo src={partner?.photo_url} />}

          <Name numberOfLines={2}>{partner?.label}</Name>
          <Description>{partner?.description}</Description>
          <Address>
            {partner?.street}, {partner?.number} - {partner?.neighborhood},{" "}
            {partner?.city} - {partner?.state}, {partner?.postal_code}
          </Address>
          <div style={{ display: "flex", flexDirection: "row" }}>
            {partner?.whatsapp && (
              <Redes
                style={{ width: 35, height: 35 }}
                onClick={() => {
                  window.open(
                    "https://api.whatsapp.com/send?phone=" + partner?.whatsapp,
                    "_blank"
                  );
                }}
              >
                <RedesImage
                  style={{ width: 35, height: 35 }}
                  src={redesSociais["Whatsapp"].icon}
                />
              </Redes>
            )}
            {partner?.whatsapp && (
              <Redes
                style={{ width: 35, height: 35, marginLeft: 0 }}
                onClick={() => {
                  window.open("tel:" + partner?.whatsapp, "_blank");
                }}
              >
                <RedesImage
                  style={{ width: 35, height: 35 }}
                  src={redesSociais["Telefone"].icon}
                />
              </Redes>
            )}
            {partner?.instagram && (
              <Redes
                style={{
                  width: 35,
                  height: 35,
                  marginLeft: 0,
                  backgroundColor: "#aaa",
                }}
                onClick={() => {
                  window.open(partner?.instagram, "_blank");
                }}
              >
                <RedesImage
                  style={{ width: 33, height: 33, marginTop: 1 }}
                  src={redesSociais["Instagram"].icon}
                />
              </Redes>
            )}
            <Redes
              style={{
                width: 35,
                height: 35,
                marginLeft: 0,
                backgroundColor: "#aaa",
              }}
              onClick={() => {
                window.open(
                  `http://maps.google.com/maps?q=loc:${partner.latitude},${partner.longitude}`,
                  "_blank"
                );
              }}
            >
              <RedesImage
                style={{ width: 33, height: 33, marginTop: 1 }}
                src={redesSociais["Maps"].icon}
              />
            </Redes>
          </div>
          {partner?.url && (
            <Button
              type="primary"
              style={{
                height: 40,
                width: "100%",
                fontSize: 16,
                marginTop: 15,
              }}
              onClick={() => {
                window.open(partner.url, "_blank");
              }}
            >
              Mais informações
            </Button>
          )}
        </div>
      </Modal>

      {isLoading && (
        <Load>
          <Spin />
        </Load>
      )}

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={zoom}
        ref={mapRef}
        onLoad={(map) => (mapRef.current = map)}
        onIdle={() => {
          const newZoom = mapRef.current?.getZoom();
          if (newZoom !== undefined) {
            setZoom(newZoom);
          }

          const bounds = mapRef.current?.getBounds();
          if (bounds) {
            const ne = bounds.getNorthEast(); // canto superior direito
            const sw = bounds.getSouthWest(); // canto inferior esquerdo

            const minLatitude = sw.lat();
            const maxLatitude = ne.lat();
            const minLongitude = sw.lng();
            const maxLongitude = ne.lng();

            const regiao = {
              minLatitude,
              maxLatitude,
              minLongitude,
              maxLongitude,
            };
            setRegiaoMax(regiao);
          }
        }}
        options={{
          fullscreenControl: false,
          zoomControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          scaleControl: false,
          rotateControl: false,
          clickableIcons: false,
          disableDefaultUI: true,
        }}
        center={regiao}
        onClick={(event) => {
          // Lógica para adicionar marcadores foi removida para focar nos filtros
        }}
      >
        {displayedPartners.map((marker) => (
          <PartnerMarker
            key={marker.id}
            marker={marker}
            onClick={handleMarkerClick}
            defaultUserImage={defaultUserImage}
          />
        ))}
      </GoogleMap>
    </Container>
  );
}
